// apps/client/src/services/audit.ts
import {
  Audit,
  AuditChecklistItem,
  AuditCreateRequest,
  AuditCreateResponse,
  AuditGetResponse,
  AuditListResponse,
  AuditUpdateRequest,
  AuditUpdateResponse,
  AuditDeleteResponse,
  AuditCompleteResponse,
  ChecklistItemUpdateRequest,
  ChecklistItemUpdateResponse,
  CorrectiveActionsListResponse,
  CorrectiveActionResolveRequest,
  CorrectiveActionResolveResponse,
  AuditWithSummary,
  CorrectiveActionItem,
  AuditReportResponse,
} from '@fsp/types';
import { apiClient } from './apiClient';

/**
 * Fetch all audits for a plan
 */
export const fetchAudits = async (
  organisationId: string,
  planId: string
): Promise<AuditWithSummary[]> => {
  const response = await apiClient.get(`/organisations/${organisationId}/plans/${planId}/audits`);

  if (!response.ok) {
    throw new Error(`Failed to fetch audits: ${response.statusText}`);
  }

  const result: AuditListResponse = await response.json();
  return result?.audits || [];
};

/**
 * Fetch a specific audit with checklist items
 */
export const fetchAudit = async (
  organisationId: string,
  planId: string,
  auditId: string
): Promise<AuditGetResponse> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/audits/${auditId}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch audit: ${response.statusText}`);
  }

  const result: AuditGetResponse = await response.json();
  return result;
};

/**
 * Create a new audit
 */
export const createAudit = async (
  organisationId: string,
  planId: string,
  data: AuditCreateRequest
): Promise<AuditCreateResponse> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/audits`,
    data
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to create audit: ${response.statusText}`);
  }

  const result: AuditCreateResponse = await response.json();
  return result;
};

/**
 * Update an audit
 */
export const updateAudit = async (
  organisationId: string,
  planId: string,
  auditId: string,
  data: AuditUpdateRequest
): Promise<Audit> => {
  const response = await apiClient.put(
    `/organisations/${organisationId}/plans/${planId}/audits/${auditId}`,
    data
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to update audit: ${response.statusText}`);
  }

  const result: AuditUpdateResponse = await response.json();
  return result.audit;
};

/**
 * Delete an audit
 */
export const deleteAudit = async (
  organisationId: string,
  planId: string,
  auditId: string
): Promise<void> => {
  const response = await apiClient.delete(
    `/organisations/${organisationId}/plans/${planId}/audits/${auditId}`
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to delete audit: ${response.statusText}`);
  }

  const result: AuditDeleteResponse = await response.json();
  if (!result.success) {
    throw new Error('Failed to delete audit');
  }
};

/**
 * Complete an audit
 */
export const completeAudit = async (
  organisationId: string,
  planId: string,
  auditId: string
): Promise<Audit> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/audits/${auditId}/complete`,
    {}
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to complete audit: ${response.statusText}`);
  }

  const result: AuditCompleteResponse = await response.json();
  return result.audit;
};

/**
 * Update a checklist item
 */
export const updateChecklistItem = async (
  organisationId: string,
  planId: string,
  auditId: string,
  itemId: string,
  data: ChecklistItemUpdateRequest
): Promise<AuditChecklistItem> => {
  const response = await apiClient.put(
    `/organisations/${organisationId}/plans/${planId}/audits/${auditId}/checklist/${itemId}`,
    data
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(
      errorResult.message || `Failed to update checklist item: ${response.statusText}`
    );
  }

  const result: ChecklistItemUpdateResponse = await response.json();
  return result.item;
};

/**
 * Fetch corrective actions with optional filters for a plan
 */
export const fetchCorrectiveActions = async (
  organisationId: string,
  planId: string,
  filters?: {
    resolved?: boolean;
    assignedToMe?: boolean;
    overdue?: boolean;
    auditId?: string;
  }
): Promise<CorrectiveActionItem[]> => {
  const params = new URLSearchParams();
  if (filters?.resolved !== undefined) params.append('resolved', String(filters.resolved));
  if (filters?.assignedToMe) params.append('assignedToMe', 'true');
  if (filters?.overdue) params.append('overdue', 'true');
  if (filters?.auditId) params.append('auditId', filters.auditId);

  const queryString = params.toString();
  const url = `/organisations/${organisationId}/plans/${planId}/corrective-actions${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch corrective actions: ${response.statusText}`);
  }

  const result: CorrectiveActionsListResponse = await response.json();
  return result.items;
};

/**
 * Resolve a corrective action
 */
export const resolveCorrectiveAction = async (
  organisationId: string,
  planId: string,
  itemId: string,
  data: CorrectiveActionResolveRequest
): Promise<AuditChecklistItem> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/corrective-actions/${itemId}/resolve`,
    data
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(
      errorResult.message || `Failed to resolve corrective action: ${response.statusText}`
    );
  }

  const result: CorrectiveActionResolveResponse = await response.json();
  return result.item;
};

/**
 * Fetch audit report with full details
 */
export const fetchAuditReport = async (
  organisationId: string,
  planId: string,
  auditId: string
): Promise<AuditReportResponse> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/audits/${auditId}/report`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch audit report: ${response.statusText}`);
  }

  const result: AuditReportResponse = await response.json();
  return result;
};
